import { createFileRoute, Link } from '@tanstack/react-router';
import { CodeBlock, InfoCallout } from '../../components/documentation';
import styles from '../../components/documentation/DocContent.module.css';
import guideStyles from './StreamingGuide.module.css';

export const Route = createFileRoute('/documentation/streaming-guide')({
  component: StreamingGuidePage,
});

function StreamingGuidePage() {
  return (
    <div className={guideStyles.page}>
      <div className={styles.breadcrumb}>Getting Started</div>
      <h1 className={styles.pageTitle}>Streaming Guide</h1>
      <p className={styles.pageSubtitle}>
        Everything you need to embed a real-time 3D stream in your page.
        This guide starts simple — a single HTML element — and builds up to
        custom overlays, bidirectional messaging, and full configurator
        patterns. By the end, you'll have the tools to ship a polished,
        production-ready experience.
      </p>

      {/* ── Part 1: Your First Stream ── */}
      <section className={styles.section}>
        <h2 className={styles.sectionTitle} id="your-first-stream">
          Part 1 — Your First Stream
        </h2>
        <p className={styles.prose}>
          The fastest way to get a live stream running. Load the component from
          a CDN, pass an admission token, and let the built-in controls handle
          the rest. No bundler, no framework — just a script tag and an HTML
          element.
        </p>
        <CodeBlock language="html" title="minimal.html">
{`<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>Pixel Stream</title>
  <style>
    body { margin: 0; background: #000; }
    pixel-stream { display: block; width: 100vw; height: 100vh; }
  </style>
</head>
<body>
  <pixel-stream></pixel-stream>

  <script src="https://cdn.interlucent.ai/dev/pixel-stream/0.0.83/pixel-stream.iife.min.js"></script>
  <script>
    const ps = document.querySelector('pixel-stream');
    ps.admissionToken = 'YOUR_TOKEN';
  </script>
</body>
</html>`}
        </CodeBlock>
        <p className={styles.prose}>
          That's it. The built-in overlay shows a play button, a spinner while
          the session is starting, and handles autoplay-blocked browsers
          automatically. Replace <code>YOUR_TOKEN</code> with an admission
          token generated server-side using the SDK (see{' '}
          <Link to="/documentation/sdk-reference">SDK Reference</Link>) and
          passed to your frontend.
        </p>
        <InfoCallout variant="tip">
          You can also install the package via npm — <code>npm install
          @interlucent/pixel-stream</code> — and import it as an ES module.
          The CDN approach is shown here for simplicity.
        </InfoCallout>
      </section>

      {/* ── Part 2: Configuring Your Stream ── */}
      <section className={styles.section}>
        <h2 className={styles.sectionTitle} id="configuration">
          Part 2 — Configuring Your Stream
        </h2>
        <p className={styles.prose}>
          Nice work — you have a stream running. Now let's customize it. Every
          aspect of session behaviour is controlled through HTML attributes.
          Here's a more deliberate configuration that specifies tolerances,
          reconnection strategy, and a specific application version.
        </p>
        <CodeBlock language="html" title="configured.html">
{`<pixel-stream
  api-endpoint="api.interlucent.ai"
  app-id="PFfFuw"
  app-version="MrM"
  reconnect-mode="recover"
  reconnect-attempts="-1"
  reconnect-strategy="exponential-backoff"
  queue-wait-tolerance="45"
  webrtc-negotiation-tolerance="10"
  swift-job-request
></pixel-stream>

<script src="https://cdn.interlucent.ai/dev/pixel-stream/0.0.83/pixel-stream.iife.min.js"></script>
<script>
  const ps = document.querySelector('pixel-stream');
  ps.admissionToken = 'YOUR_TOKEN';
</script>`}
        </CodeBlock>

        <div className={styles.conceptList}>
          <div className={styles.conceptItem}>
            <div className={styles.conceptTerm}>api-endpoint</div>
            <div className={styles.conceptDef}>
              The Interlucent API host. Defaults
              to <code>api.interlucent.ai</code> — you only need to set this
              if you are using a custom deployment.
            </div>
          </div>
          <div className={styles.conceptItem}>
            <div className={styles.conceptTerm}>reconnect-mode</div>
            <div className={styles.conceptDef}>
              <code>none</code> — do not reconnect.{' '}
              <code>recover</code> — attempt to rejoin the same session.{' '}
              <code>always</code> — start a new session if recovery fails.
            </div>
          </div>
          <div className={styles.conceptItem}>
            <div className={styles.conceptTerm}>swift-job-request</div>
            <div className={styles.conceptDef}>
              Sends the GPU job request during the token exchange rather than
              waiting for the session WebSocket to open. Saves 200–400 ms on
              cold starts.
            </div>
          </div>
          <div className={styles.conceptItem}>
            <div className={styles.conceptTerm}>queue-wait-tolerance</div>
            <div className={styles.conceptDef}>
              Seconds to wait for a GPU worker to become available before the
              session is considered failed. Set this based on your expected
              fleet utilisation.
            </div>
          </div>
        </div>

        <InfoCallout variant="info">
          All attributes map to JavaScript properties using camelCase.
          For example, <code>reconnect-mode</code> is accessible as{' '}
          <code>el.reconnectMode</code> and <code>queue-wait-tolerance</code>{' '}
          as <code>el.queueWaitTolerance</code>.
        </InfoCallout>
      </section>

      {/* ── Part 3: Building a Custom Overlay ── */}
      <section className={styles.section}>
        <h2 className={styles.sectionTitle} id="custom-overlay">
          Part 3 — Building a Custom Overlay
        </h2>
        <p className={styles.prose}>
          The default controls are great for prototyping, but most production
          embeds need branded UI. The component exposes an{' '}
          <code>overlay</code> slot that replaces the built-in controls
          entirely. You drive the UI by reacting to the component's{' '}
          <code>status</code> attribute, which stays in sync with the session
          lifecycle.
        </p>
        <CodeBlock language="html" title="custom-overlay.html">
{`<pixel-stream id="ps">
  <div slot="overlay" class="overlay">

    <!-- Idle: show a branded play button -->
    <div class="state state-idle">
      <h2>Ready to Launch</h2>
      <button onclick="document.getElementById('ps').play()">
        Start Experience
      </button>
    </div>

    <!-- Queued / negotiating: show a loading state -->
    <div class="state state-pending">
      <div class="spinner"></div>
      <p>Preparing your session&hellip;</p>
      <button onclick="document.getElementById('ps').cancel()">
        Cancel
      </button>
    </div>

    <!-- Autoplay blocked: user gesture needed -->
    <div class="state state-ready">
      <button onclick="document.getElementById('ps').play()">
        Resume
      </button>
    </div>

    <!-- Error: offer retry -->
    <div class="state state-error">
      <p>Something went wrong.</p>
      <button onclick="document.getElementById('ps').play()">
        Try Again
      </button>
    </div>

  </div>
</pixel-stream>`}
        </CodeBlock>
        <p className={styles.prose}>
          The <code>&lt;pixel-stream&gt;</code> element sets a{' '}
          <code>status</code> attribute on itself as the session progresses
          through its lifecycle: <code>idle</code>, <code>connecting</code>,{' '}
          <code>authenticating</code>, <code>connected</code>,{' '}
          <code>queued</code>, <code>rendezvoused</code>,{' '}
          <code>negotiating</code>, <code>streaming</code>, <code>ready</code>,{' '}
          <code>failed</code>, <code>ended</code>. Use CSS attribute selectors
          to show and hide your overlay states.
        </p>
        <CodeBlock language="css" title="overlay-states.css">
{`/* Hide all states by default */
.state { display: none; }

/* Show the right state based on the component's status */
pixel-stream[status="idle"]            .state-idle,
pixel-stream[status="connected"]       .state-idle    { display: flex; }

pixel-stream[status="connecting"]      .state-pending,
pixel-stream[status="authenticating"]  .state-pending,
pixel-stream[status="queued"]          .state-pending,
pixel-stream[status="rendezvoused"]    .state-pending,
pixel-stream[status="negotiating"]     .state-pending { display: flex; }

pixel-stream[status="ready"]           .state-ready   { display: flex; }
pixel-stream[status="failed"]          .state-error   { display: flex; }
pixel-stream[status="ended"]           .state-error   { display: flex; }

/* Hide overlay during streaming */
pixel-stream[status="streaming"]       .overlay { display: none; }`}
        </CodeBlock>
        <InfoCallout variant="tip">
          When a slotted overlay is present, the component's built-in controls
          are fully replaced and the overlay container remains visible at all
          times — including during streaming. Your overlay takes responsibility
          for calling <code>play()</code>, <code>cancel()</code>, and{' '}
          <code>stop()</code> at the appropriate times, and for hiding itself
          during streaming via CSS (e.g.{' '}
          <code>pixel-stream[status="streaming"] .overlay {'{'} display: none {'}'}</code>).
        </InfoCallout>
      </section>

      {/* ── Part 4: Driving It from JavaScript ── */}
      <section className={styles.section}>
        <h2 className={styles.sectionTitle} id="programmatic-control">
          Part 4 — Driving It from JavaScript
        </h2>
        <p className={styles.prose}>
          Your overlay looks great — now let's go deeper. For richer
          integrations — React wrappers, multi-step flows, or analytics —
          you'll want to drive the component from JavaScript. Every attribute
          has a corresponding property, and the session lifecycle methods are
          available directly on the element.
        </p>
        <CodeBlock language="javascript" title="programmatic.js">
{`const ps = document.querySelector('pixel-stream');

// Set the admission token (generated server-side via the SDK)
ps.admissionToken = token;  // token for app PFfFuw, version MrM
ps.reconnectMode = 'recover';
ps.reconnectAttempts = -1; // unlimited

// Start streaming — connects the session and requests a GPU worker.
// The component auto-connects when admissionToken is set, so play()
// just sends the job request. If using no-auto-connect, play()
// starts the session first.
await ps.play();

// Stop gracefully
ps.stop('user-navigated-away');`}
        </CodeBlock>
        <p className={styles.prose}>
          You can also inspect the component's read-only state at any time.
        </p>
        <CodeBlock language="javascript">
{`console.log(ps.status);              // 'streaming', 'queued', etc.
console.log(ps.sessionId);           // current session ID
console.log(ps.agentId);             // your agent ID in this session
console.log(ps.isAdmitted);          // true once token exchange succeeds
console.log(ps.failureReason);       // null, or a description of the failure
console.log(ps.streamStartedAt);     // timestamp (ms) when streaming began, or 0
console.log(ps.lastUserInteraction); // timestamp of last user input (seeded at stream start)`}
        </CodeBlock>
      </section>

      {/* ── Part 5: Receiving messages ── */}
      <section className={styles.section}>
        <h2 className={styles.sectionTitle} id="receiving-messages">
          Part 5 — Listening for Messages from the Game
        </h2>
        <p className={styles.prose}>
          The Unreal Engine application can send structured messages back to the
          browser through the WebRTC data channel. These arrive as DOM events
          on the <code>&lt;pixel-stream&gt;</code> element. The two most common
          patterns are <strong>command responses</strong> and{' '}
          <strong>echo tests</strong>.
        </p>

        <h3 className={styles.subsectionTitle} id="ue-command-response">
          Command Responses
        </h3>
        <p className={styles.prose}>
          When UE processes a <code>Command</code> or <code>Response</code>{' '}
          message and sends a reply, it arrives as a{' '}
          <code>ue-command-response</code> event. The payload is whatever JSON
          the UE application serialised.
        </p>
        <CodeBlock language="javascript" title="receiving-responses.js">
{`const ps = document.querySelector('pixel-stream');

ps.addEventListener('ue-command-response', (event) => {
  const data = event.detail;
  console.log('UE responded:', data);

  // Example: the game sends back a configuration result
  // { "status": "ok", "selectedColor": "#FF5500" }
  if (data.status === 'ok') {
    updateUI(data);
  }
});`}
        </CodeBlock>

        <h3 className={styles.subsectionTitle} id="lifecycle-events">
          Lifecycle Events
        </h3>
        <p className={styles.prose}>
          Beyond game messages, the component dispatches events for every stage
          of the connection. Here are the most useful ones for building
          reactive UI.
        </p>
        <CodeBlock language="javascript" title="lifecycle-events.js">
{`// The derived status changed (idle → queued → streaming → …)
ps.addEventListener('status-change', (e) => {
  console.log('Status:', e.detail.newStatus);
  console.log('Previous:', e.detail.oldStatus);
});`}
        </CodeBlock>

        <h3 className={styles.subsectionTitle} id="status-values">
          Status Values
        </h3>
        <p className={styles.prose}>
          The <code>status</code> property (and the matching HTML attribute) is
          a single derived value that combines session state, job state, and
          stream state into one progression. These are all the possible values,
          in typical order:
        </p>
        <div className={styles.conceptList}>
          <div className={styles.conceptItem}>
            <div className={styles.conceptTerm}>idle</div>
            <div className={styles.conceptDef}>
              Initial state. No session has been started yet.
            </div>
          </div>
          <div className={styles.conceptItem}>
            <div className={styles.conceptTerm}>connecting</div>
            <div className={styles.conceptDef}>
              The session WebSocket is being opened, or the component is
              reconnecting after a drop.
            </div>
          </div>
          <div className={styles.conceptItem}>
            <div className={styles.conceptTerm}>authenticating</div>
            <div className={styles.conceptDef}>
              The admission token is being exchanged and verified. This is the
              step between the WebSocket handshake and full session readiness.
            </div>
          </div>
          <div className={styles.conceptItem}>
            <div className={styles.conceptTerm}>connected</div>
            <div className={styles.conceptDef}>
              The session WebSocket is authenticated and ready. The built-in
              overlay shows a play button here. Call{' '}
              <code>play()</code> to request a GPU worker, or the built-in
              controls will do it for you.
            </div>
          </div>
          <div className={styles.conceptItem}>
            <div className={styles.conceptTerm}>queued</div>
            <div className={styles.conceptDef}>
              A GPU worker has been requested and the session is waiting for a
              GPU worker to become available. Duration depends on fleet
              availability and your <code>queue-wait-tolerance</code>.
            </div>
          </div>
          <div className={styles.conceptItem}>
            <div className={styles.conceptTerm}>rendezvoused</div>
            <div className={styles.conceptDef}>
              A GPU worker has been matched to this session. The two peers are
              about to begin WebRTC negotiation.
            </div>
          </div>
          <div className={styles.conceptItem}>
            <div className={styles.conceptTerm}>negotiating</div>
            <div className={styles.conceptDef}>
              WebRTC connection negotiation is in progress — connection paths
              are being tested and the best route is being selected.
            </div>
          </div>
          <div className={styles.conceptItem}>
            <div className={styles.conceptTerm}>streaming</div>
            <div className={styles.conceptDef}>
              Video frames are flowing and the data channel is open. This is
              the main interactive state — the user is seeing and interacting
              with the application.
            </div>
          </div>
          <div className={styles.conceptItem}>
            <div className={styles.conceptTerm}>ready</div>
            <div className={styles.conceptDef}>
              The stream is available but playback was blocked by the browser's
              autoplay policy. A user gesture is needed — call{' '}
              <code>play()</code> from a click handler.
            </div>
          </div>
          <div className={styles.conceptItem}>
            <div className={styles.conceptTerm}>interrupted</div>
            <div className={styles.conceptDef}>
              The media pipeline stalled (e.g. network congestion dropped
              tracks) but the session WebSocket is still alive. The component
              will attempt recovery automatically.
            </div>
          </div>
          <div className={styles.conceptItem}>
            <div className={styles.conceptTerm}>recovering</div>
            <div className={styles.conceptDef}>
              Active media recovery is in progress — re-subscribing to the
              streamer and re-establishing the connection.
            </div>
          </div>
          <div className={styles.conceptItem}>
            <div className={styles.conceptTerm}>failed</div>
            <div className={styles.conceptDef}>
              A terminal state. The session could not be established or
              recovery was exhausted. Check <code>failureReason</code> for
              details. Calling <code>play()</code> automatically clears
              the error state and starts a new attempt.
            </div>
          </div>
          <div className={styles.conceptItem}>
            <div className={styles.conceptTerm}>ended</div>
            <div className={styles.conceptDef}>
              A terminal state. The session concluded gracefully — either the
              user left, the server shut down, or the linger timeout expired.
            </div>
          </div>
        </div>

        <InfoCallout variant="tip">
          The typical happy-path progression is: <code>idle</code> →{' '}
          <code>connecting</code> → <code>authenticating</code> →{' '}
          <code>connected</code> → <code>queued</code> →{' '}
          <code>rendezvoused</code> → <code>negotiating</code> →{' '}
          <code>streaming</code>. The{' '}
          <code>ready</code> state only appears when the browser blocks
          autoplay.
        </InfoCallout>
      </section>

      {/* ── Part 6: Talking to the Game ── */}
      <section className={styles.section}>
        <h2 className={styles.sectionTitle} id="sending-messages">
          Part 6 — Talking to the Game
        </h2>
        <p className={styles.prose}>
          Once the data channel is open, you can send structured messages to
          the Unreal Engine application. There are several message types, each
          suited to a different integration pattern.
        </p>

        <h3 className={styles.subsectionTitle} id="send-ui-interaction">
          UI Interactions
        </h3>
        <p className={styles.prose}>
          The most common message type. Send a JSON descriptor that UE receives
          as a <code>UIInteraction</code>. Use this for application-level
          commands like changing colours, selecting options, or triggering
          animations.
        </p>
        <CodeBlock language="javascript" title="ui-interaction.js">
{`// Wait for the data channel to be ready
ps.addEventListener('data-channel-open', () => {

  // Send a colour change to the game
  ps.sendUIInteraction({
    action: 'setColor',
    target: 'car-body',
    color: '#FF5500'
  });

  // Send a camera angle request
  ps.sendUIInteraction({
    action: 'setCamera',
    preset: 'front-quarter'
  });
});`}
        </CodeBlock>

        <h3 className={styles.subsectionTitle} id="send-command">
          Commands (UE 5.4+)
        </h3>
        <p className={styles.prose}>
          For Unreal Engine 5.4 and later, use the <code>Command</code> message
          type. These are handled by UE's built-in command router and support
          structured key-value payloads.
        </p>
        <CodeBlock language="javascript">
{`// Set the viewport resolution
ps.sendCommand({
  'Resolution.Width': 1920,
  'Resolution.Height': 1080
});

// Execute a console command
ps.sendCommand({
  'Console': 'stat fps'
});`}
        </CodeBlock>


        <InfoCallout variant="warning">
          Always wait for the <code>data-channel-open</code> event before
          sending messages. Calls made before the data channel is ready will
          be silently dropped.
        </InfoCallout>
      </section>

      {/* ── Part 7: The Full Picture ── */}
      <section className={styles.section}>
        <h2 className={styles.sectionTitle} id="full-example">
          Part 7 — The Full Picture
        </h2>
        <p className={styles.prose}>
          You've built up every piece individually — now let's see them
          working together. Here's a complete example: a CDN-loaded component
          with a custom overlay, bidirectional messaging, and lifecycle event
          handling. This pattern works well for product configurators,
          interactive showcases, and kiosk-style embeds.
        </p>
        <CodeBlock language="html" title="full-example.html">
{`<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>3D Configurator</title>
  <style>
    body { margin: 0; background: #0a0a0a; font-family: system-ui, sans-serif; }
    pixel-stream { display: block; width: 100vw; height: 100vh; }

    .overlay {
      width: 100%; height: 100%;
      display: grid; place-items: center;
      color: #fff;
    }
    .state { display: none; flex-direction: column; align-items: center; gap: 16px; }
    pixel-stream[status="idle"]      .state-idle,
    pixel-stream[status="connected"] .state-idle     { display: flex; }
    pixel-stream[status="queued"]    .state-pending,
    pixel-stream[status="negotiating"] .state-pending { display: flex; }
    pixel-stream[status="failed"]    .state-error     { display: flex; }

    .sidebar {
      position: fixed; right: 0; top: 0; bottom: 0; width: 280px;
      background: rgba(0,0,0,0.85); padding: 24px;
      display: none; flex-direction: column; gap: 12px; z-index: 10;
    }
    .sidebar.visible { display: flex; }

    .swatch {
      width: 40px; height: 40px; border: 2px solid transparent;
      cursor: pointer; transition: border-color 0.2s;
    }
    .swatch:hover, .swatch.active { border-color: #fff; }
  </style>
</head>
<body>
  <pixel-stream id="ps"
    controls
    swift-job-request
    queue-wait-tolerance="45"
  >
    <div slot="overlay" class="overlay">
      <div class="state state-idle">
        <button onclick="document.getElementById('ps').play()">
          Launch Configurator
        </button>
      </div>
      <div class="state state-pending">
        <p>Starting your session&hellip;</p>
      </div>
      <div class="state state-error">
        <p>Connection failed.</p>
        <button onclick="document.getElementById('ps').play()">
          Retry
        </button>
      </div>
    </div>
  </pixel-stream>

  <div id="sidebar" class="sidebar">
    <h3 style="color:#fff; margin:0;">Colour</h3>
    <div style="display:flex; gap:8px; flex-wrap:wrap;" id="swatches"></div>
    <div id="response-log" style="margin-top:auto; font-size:11px; color:#888;"></div>
  </div>

  <script src="https://cdn.interlucent.ai/dev/pixel-stream/0.0.83/pixel-stream.iife.min.js"></script>
  <script>
    const ps = document.getElementById('ps');
    ps.admissionToken = 'YOUR_TOKEN';

    const sidebar = document.getElementById('sidebar');
    const swatches = document.getElementById('swatches');
    const log = document.getElementById('response-log');

    const COLORS = ['#FF5500', '#0055FF', '#00CC66', '#FFD700', '#CC00FF', '#FFFFFF'];

    // Build colour swatches
    COLORS.forEach(color => {
      const el = document.createElement('div');
      el.className = 'swatch';
      el.style.background = color;
      el.onclick = () => {
        document.querySelectorAll('.swatch').forEach(s => s.classList.remove('active'));
        el.classList.add('active');
        ps.sendUIInteraction({ action: 'setColor', color });
      };
      swatches.appendChild(el);
    });

    // Show sidebar once streaming begins
    ps.addEventListener('status-change', (e) => {
      sidebar.classList.toggle('visible', e.detail.newStatus === 'streaming');
    });

    // Handle responses from UE
    ps.addEventListener('ue-command-response', (e) => {
      log.textContent = 'UE: ' + JSON.stringify(e.detail);
    });
  </script>
</body>
</html>`}
        </CodeBlock>

        <InfoCallout variant="tip">
          For more overlay patterns — branded logos, idle timers, grid layouts,
          and connection diagnostics — see the{' '}
          <code className={styles.mono}>pixel-stream/examples/</code> directory.
          Each numbered example is a standalone HTML file you can open directly
          in a browser.
        </InfoCallout>
      </section>

      {/* ── Part 8: Collaboration ── */}
      <section className={styles.section}>
        <h2 className={styles.sectionTitle} id="collaboration">
          Part 8 — Collaboration
        </h2>
        <p className={styles.prose}>
          Multiple users can share the same streaming session. The session
          owner generates a short-lived invite token and shares it with a
          collaborator, who uses it as their admission token to join the same
          GPU worker.
        </p>

        <h3 className={styles.subsectionTitle} id="collab-host">
          Host: Generating an Invite
        </h3>
        <p className={styles.prose}>
          Once the stream is active, call <code>requestInvite()</code> and
          listen for the <code>invite-token</code> event. The token is a
          short-lived JWT that a collaborator uses as their admission token.
        </p>
        <CodeBlock language="html" title="host.html">
{`<pixel-stream id="stream"
  controls
  api-endpoint="api.interlucent.ai"
  admission-token="YOUR_ADMISSION_TOKEN"
></pixel-stream>

<button id="invite-btn" disabled>Generate Invite</button>
<input id="token-box" readonly placeholder="Invite token will appear here...">

<script src="https://cdn.interlucent.ai/dev/pixel-stream/0.0.83/pixel-stream.iife.min.js"></script>
<script>
  const stream = document.getElementById('stream');
  const inviteBtn = document.getElementById('invite-btn');
  const tokenBox = document.getElementById('token-box');

  // Enable the button once streaming
  stream.addEventListener('status-change', (e) => {
    inviteBtn.disabled = e.detail.newStatus !== 'streaming';
  });

  // Request an invite token
  inviteBtn.addEventListener('click', () => stream.requestInvite());

  // Display the token when it arrives
  stream.addEventListener('invite-token', (e) => {
    tokenBox.value = e.detail.token;
  });
</script>`}
        </CodeBlock>

        <h3 className={styles.subsectionTitle} id="collab-join">
          Guest: Joining with an Invite
        </h3>
        <p className={styles.prose}>
          The guest passes the invite token as their{' '}
          <code>admission-token</code>. The component auto-detects the token
          type and joins the existing session. Guests receive the stream and
          can send input, but cannot request jobs or generate further invites.
        </p>
        <CodeBlock language="html" title="guest.html">
{`<pixel-stream id="stream"
  no-auto-connect
  controls
  api-endpoint="api.interlucent.ai"
></pixel-stream>

<input id="token-input" placeholder="Paste invite token here...">
<button id="join-btn">Join</button>

<script src="https://cdn.interlucent.ai/dev/pixel-stream/0.0.83/pixel-stream.iife.min.js"></script>
<script>
  const stream = document.getElementById('stream');
  const joinBtn = document.getElementById('join-btn');

  joinBtn.addEventListener('click', () => {
    const token = document.getElementById('token-input').value.trim();
    if (token) stream.setAttribute('admission-token', token);
  });

  // Track participants
  stream.addEventListener('participant-joined', (e) => {
    console.log('Joined:', e.detail.agentId, e.detail.agentRole);
  });

  stream.addEventListener('participant-left', (e) => {
    console.log('Left:', e.detail.agentId, e.detail.reason);
  });
</script>`}
        </CodeBlock>

        <InfoCallout variant="info">
          Both host and guests receive{' '}
          <code>participant-joined</code> and <code>participant-left</code>{' '}
          events for all participants, including the GPU worker. A guest can
          join before the worker arrives — the stream appears when it does.
        </InfoCallout>
      </section>

      {/* ── Reference ── */}
      <section className={styles.section}>
        <h2 className={styles.sectionTitle} id="quick-reference">
          Quick Reference
        </h2>

        <h3 className={styles.subsectionTitle} id="ref-methods">
          Methods
        </h3>
        <div className={styles.conceptList}>
          <div className={styles.conceptItem}>
            <div className={styles.conceptTerm}>play()</div>
            <div className={styles.conceptDef}>
              Start or resume streaming. Handles all states: requests a GPU
              worker when connected, starts the session if needed, resumes
              video if autoplay was blocked, or retries after an error.
              Must be called from a user gesture.
            </div>
          </div>
          <div className={styles.conceptItem}>
            <div className={styles.conceptTerm}>cancel()</div>
            <div className={styles.conceptDef}>
              Cancel a pending job request while still queued.
            </div>
          </div>
          <div className={styles.conceptItem}>
            <div className={styles.conceptTerm}>stop(reason?)</div>
            <div className={styles.conceptDef}>
              Stop the session gracefully. The GPU worker may linger for
              reconnection depending on tolerances.
            </div>
          </div>
          <div className={styles.conceptItem}>
            <div className={styles.conceptTerm}>sendUIInteraction(descriptor)</div>
            <div className={styles.conceptDef}>
              Send a JSON object to UE as a UIInteraction message.
            </div>
          </div>
          <div className={styles.conceptItem}>
            <div className={styles.conceptTerm}>sendCommand(command)</div>
            <div className={styles.conceptDef}>
              Send a JSON object to UE as a Command message (UE 5.4+).
            </div>
          </div>
          <div className={styles.conceptItem}>
            <div className={styles.conceptTerm}>requestInvite()</div>
            <div className={styles.conceptDef}>
              Generate a short-lived invite token (5-minute window) that a
              collaborator can use to join this session. Listen for the{' '}
              <code>invite-token</code> event to receive the token. Only
              available to the session owner.
            </div>
          </div>
        </div>

        <h3 className={styles.subsectionTitle} id="ref-events">
          Key Events
        </h3>
        <div className={styles.conceptList}>
          <div className={styles.conceptItem}>
            <div className={styles.conceptTerm}>status-change</div>
            <div className={styles.conceptDef}>
              Fired when the derived status changes. Detail
              includes <code>newStatus</code> and <code>oldStatus</code>.
            </div>
          </div>
          <div className={styles.conceptItem}>
            <div className={styles.conceptTerm}>data-channel-open</div>
            <div className={styles.conceptDef}>
              The WebRTC data channel is ready — you can now send messages to
              UE.
            </div>
          </div>
          <div className={styles.conceptItem}>
            <div className={styles.conceptTerm}>ue-command-response</div>
            <div className={styles.conceptDef}>
              UE sent a Response or Command message back. Payload is in{' '}
              <code>event.detail</code>.
            </div>
          </div>
          <div className={styles.conceptItem}>
            <div className={styles.conceptTerm}>session-ready</div>
            <div className={styles.conceptDef}>
              Session WebSocket authenticated. Detail
              includes <code>sessionId</code>, <code>agentId</code>,{' '}
              <code>participants</code>.
            </div>
          </div>
          <div className={styles.conceptItem}>
            <div className={styles.conceptTerm}>session-ended</div>
            <div className={styles.conceptDef}>
              Session terminated. Detail includes <code>reason</code>.
            </div>
          </div>
          <div className={styles.conceptItem}>
            <div className={styles.conceptTerm}>invite-token</div>
            <div className={styles.conceptDef}>
              Fired after <code>requestInvite()</code>. Detail
              includes <code>token</code> — a JWT the collaborator
              uses as their admission token.
            </div>
          </div>
          <div className={styles.conceptItem}>
            <div className={styles.conceptTerm}>participant-joined</div>
            <div className={styles.conceptDef}>
              A participant joined the session. Detail
              includes <code>agentId</code> and <code>agentRole</code>.
            </div>
          </div>
          <div className={styles.conceptItem}>
            <div className={styles.conceptTerm}>participant-left</div>
            <div className={styles.conceptDef}>
              A participant left the session. Detail
              includes <code>agentId</code> and <code>reason</code>.
            </div>
          </div>
        </div>

        <h3 className={styles.subsectionTitle} id="ref-attributes">
          Key Attributes
        </h3>
        <div className={styles.conceptList}>
          <div className={styles.conceptItem}>
            <div className={styles.conceptTerm}>api-endpoint</div>
            <div className={styles.conceptDef}>
              Platform API hostname
              (e.g. <code>api.interlucent.ai</code>).
            </div>
          </div>
          <div className={styles.conceptItem}>
            <div className={styles.conceptTerm}>admission-token</div>
            <div className={styles.conceptDef}>
              JWT that authorises the session. Encodes the target application and
              version.
            </div>
          </div>
          <div className={styles.conceptItem}>
            <div className={styles.conceptTerm}>pointer-lock</div>
            <div className={styles.conceptDef}>
              Enables FPS-style cursor capture. Click the stream to lock, press
              Escape to unlock. Combine
              with <code>pointer-lock-release</code> for hold-to-lock behaviour
              (lock on mousedown, release on mouseup).
            </div>
          </div>
          <div className={styles.conceptItem}>
            <div className={styles.conceptTerm}>pointer-lock-release</div>
            <div className={styles.conceptDef}>
              When combined with <code>pointer-lock</code>, the cursor is
              captured only while a mouse button is held. Useful for orbit /
              drag-look controls instead of permanent FPS-style capture.
            </div>
          </div>
          <div className={styles.conceptItem}>
            <div className={styles.conceptTerm}>native-touch</div>
            <div className={styles.conceptDef}>
              Enables real multi-touch input with per-finger tracking, force
              data, and up to 10 simultaneous contacts. Without this attribute,
              touches are converted to mouse events.
            </div>
          </div>
          <div className={styles.conceptItem}>
            <div className={styles.conceptTerm}>suppress-browser-keys</div>
            <div className={styles.conceptDef}>
              Prevents default browser behaviour for keys like Tab, F5, and
              arrow keys so they are forwarded to the stream instead.
            </div>
          </div>
          <div className={styles.conceptItem}>
            <div className={styles.conceptTerm}>force-relay</div>
            <div className={styles.conceptDef}>
              Forces all WebRTC traffic through a TURN relay server. Useful when
              direct peer connections are blocked by firewalls.
            </div>
          </div>
          <div className={styles.conceptItem}>
            <div className={styles.conceptTerm}>resolution-clamp</div>
            <div className={styles.conceptDef}>
              Limits the stream resolution
              (e.g. <code>1920x1080</code>). Accepts <code>WxH</code> format.
            </div>
          </div>
          <div className={styles.conceptItem}>
            <div className={styles.conceptTerm}>controls</div>
            <div className={styles.conceptDef}>
              Shows built-in on-screen controls for volume, fullscreen,
              and microphone.
            </div>
          </div>
          <div className={styles.conceptItem}>
            <div className={styles.conceptTerm}>use-mic</div>
            <div className={styles.conceptDef}>
              Requests microphone access when the stream starts. If the
              browser denies permission, the attribute is automatically
              removed. Requires a secure context (HTTPS or localhost).
              Toggle at runtime via <code>el.useMic</code>.
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
